// The side menu on the edit tab.
//
// The main entrypoint is `EditMenu`, which is the high-level collapsable side menu for
// elements in the tree view.
//
// Containers for fields/groups are only defined here as interfaces for easier testing.

import {
  assert,
  type BasicData,
  type ElementIdentifier,
  type Group,
  type Rule,
  type RuleArbeitskopie,
  type RuleReference,
  type Schema,
} from "./project";
import { RuleEditor } from "./rule-editor";
import { type ProjectStore, getRelativeChildPath } from "./store";
import {
  parseAbleitungsmodifikationenRepraesentation,
  parseAbleitungsmodifikationenStruktur,
  type xdf3,
} from "./xdatenfelder";

export class EditMenu {
  readonly root: HTMLElement;

  private store: ProjectStore;

  private activeElement: ElementIdentifier | undefined = undefined;
  private activeMenu:
    | SchemaMenu
    | GroupMenu
    | FieldMenu
    | RuleMenu
    | undefined = undefined;

  constructor(store: ProjectStore) {
    this.store = store;

    this.root = document.createElement("div");
    this.root.classList.add("card", "flex-grow-1");

    this.store.addEventListener("active", this.onActive);
    this.onActive();
  }

  public detach() {
    this.store.removeEventListener("active", this.onActive);
    this.closeActiveMenu();
  }

  private closeActiveMenu() {
    if (this.activeMenu !== undefined) {
      this.activeMenu.detach();
      this.activeMenu = undefined;
      this.root.innerHTML = "";
    }
  }

  private onActive = () => {
    const activeElement = this.store.activeElement;

    // Do nothing if the active element did not change.
    if (this.activeElement === activeElement) {
      return;
    }

    this.activeElement = activeElement;

    this.closeActiveMenu();

    let menuHeader: HTMLElement | undefined = undefined;
    if (activeElement.type === "schema") {
      menuHeader = createMenuHeader(activeElement);
      this.activeMenu = new SchemaMenu(this.store);
    } else if (activeElement.type === "field") {
      menuHeader = createMenuHeader(activeElement);
      this.activeMenu = new FieldMenu(this.store, activeElement.fimId);
    } else if (activeElement.type === "group") {
      menuHeader = createMenuHeader(activeElement);
      this.activeMenu = new GroupMenu(this.store, activeElement.fimId);
    } else if (activeElement.type === "rule") {
      const parent = this.store.getRuleParent(activeElement.fimId);

      menuHeader = createMenuHeader(parent, activeElement.fimId, () => {
        this.closeActiveMenu();
        this.store.setActiveElement(parent);
      });
      this.activeMenu = new RuleMenu(this.store, activeElement.fimId);
    } else {
      this.activeMenu = undefined;
    }

    if (this.activeMenu !== undefined) {
      assert(menuHeader !== undefined);

      this.root.append(menuHeader);
      this.root.append(this.activeMenu.root);
    }
  };
}

interface ElementPickerState {
  label: string;
  path: string;
}

class MenuContainer {
  readonly root: HTMLElement;

  // TODO: Make `disabled`updateable
  constructor(private disabled: boolean) {
    this.root = document.createElement("div");
  }

  public elementPicker(
    name: string,
    onPick: () => void,
  ): (value: ElementPickerState | undefined) => void {
    const input = document.createElement("input");
    input.classList.add("form-control");
    input.readOnly = true;
    if (this.disabled) {
      input.disabled = true;
    }

    const button = document.createElement("button");
    button.classList.add("btn", "btn-light", "border");
    button.innerHTML += '<i class="bi bi-hand-index"></i>';
    button.addEventListener("click", onPick);
    if (this.disabled) {
      button.disabled = true;
    }

    const inputGroup = document.createElement("div");
    inputGroup.classList.add("input-group", "input-group-sm");
    inputGroup.append(input, button);

    const label = this.createLabel(name);

    const path = document.createElement("div");
    path.classList.add("form-text");

    const container = document.createElement("div");
    container.classList.add("mb-3");
    container.append(label, inputGroup, path);
    this.addElement(container);

    const update = (value: ElementPickerState | undefined) => {
      if (value !== undefined) {
        input.value = value.label;
        path.textContent = value.path;
      } else {
        input.value = "";
        path.textContent = "";
      }
    };

    return update;
  }

  public select(
    name: string,
    options: [string, string][],
    onChange: (value: string) => void,
  ): (value: string) => void {
    const select = document.createElement("select");
    select.classList.add("form-select", "form-select-sm");
    if (this.disabled) {
      select.disabled = true;
    }

    for (const [value, label] of options) {
      const optionElement = document.createElement("option");
      optionElement.value = value;
      optionElement.textContent = label;

      select.append(optionElement);
    }

    select.addEventListener("change", () => {
      onChange(select.value);
    });

    const label = this.createLabel(name);

    const container = document.createElement("div");
    container.classList.add("mb-3");
    container.append(label, select);
    this.addElement(container);

    const update = (value: string) => {
      select.value = value;
    };

    return update;
  }

  public textInput(
    name: string,
    onChange: (value: string) => void,
  ): (value: string | undefined) => void {
    const container = document.createElement("div");
    container.classList.add("mb-3");
    container.innerHTML = `
      <label class="form-label mb-2 fw-semibold"></label>
      <input class="form-control form-control-sm">
    `;

    const label = container.querySelector("label") as HTMLLabelElement;
    label.textContent = name;

    const input = container.querySelector("input") as HTMLInputElement;
    input.addEventListener("input", () => {
      onChange(input.value);
    });
    if (this.disabled) {
      input.disabled = true;
    }

    this.addElement(container);

    const update = (value: string | undefined) => {
      input.value = value || "";
    };

    return update;
  }

  public textArea(
    name: string,
    onChange: (value: string) => void,
  ): (value: string | undefined) => void {
    const container = document.createElement("div");
    container.classList.add("mb-3");
    container.innerHTML = `
      <label class="form-label mb-2 fw-semibold"></label>
      <textarea class="form-control form-control-sm" rows="5"></textarea>
    `;

    const label = container.querySelector("label") as HTMLLabelElement;
    label.textContent = name;

    const input = container.querySelector("textarea") as HTMLTextAreaElement;
    input.addEventListener("input", () => {
      onChange(input.value);
    });
    if (this.disabled) {
      input.disabled = true;
    }

    this.addElement(container);

    const update = (value: string | undefined) => {
      input.value = value || "";
    };

    return update;
  }

  /**
   * Create all of the necessary inputs to edit basic data.
   * This will return an update function which should be called
   * whenever the basic data is updated.
   */
  public basicElementData(
    editElement: (editCallback: (basicData: BasicData) => void) => void,
  ): (basicData: BasicData) => void {
    const updateName = this.textInput("Name", (value) => {
      editElement((basicData) => {
        basicData.name = value;
      });
    });

    const updateBeschreibung = this.textArea("Beschreibung", (value) => {
      editElement((basicData) => {
        basicData.beschreibung = value;
      });
    });

    const updateDefinition = this.textArea("Definition", (value) => {
      editElement((basicData) => {
        basicData.definition = value;
      });
    });

    const update = (basicData: BasicData) => {
      updateName(basicData.name);
      updateBeschreibung(basicData.beschreibung);
      updateDefinition(basicData.definition);
    };

    return update;
  }

  private createLabel(name: string): HTMLElement {
    const label = document.createElement("label");
    label.classList.add("form-label", "mb-2", "fw-semibold");
    label.textContent = name;

    return label;
  }

  public addElement(element: HTMLElement) {
    this.root.append(element);
  }
}

function createMenuHeader(
  rootElement: ElementIdentifier,
  childId?: string | undefined,
  onClick?: () => void,
) {
  let icon = "";
  if (rootElement.type === "schema") {
    icon = "bi-file-earmark";
  } else if (rootElement.type === "group") {
    icon = "bi-collection";
  } else if (rootElement.type === "field") {
    icon = "bi-input-cursor";
  }

  const header = document.createElement("div");
  header.classList.add(
    "px-3",
    "py-2",
    "d-flex",
    "flex-row",
    "align-items-center",
    "gap-2",
  );

  header.innerHTML = `
    <i class="bi ${icon}"></i>
  `;

  if (childId !== undefined) {
    assert(onClick !== undefined);

    header.innerHTML += `
            <a href="#" id="back"></a>
            <i class="bi bi-chevron-right"></i>
        `;
    header.append(childId);

    const backLink = header.querySelector("#back") as HTMLAnchorElement;
    backLink.innerText = rootElement.fimId;
    backLink.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  } else {
    header.append(rootElement.fimId);
  }

  return header;
}

class TabMenu {
  public readonly root: HTMLElement;

  private content: HTMLElement;
  private linkElements: HTMLAnchorElement[] = [];
  private contentElements: HTMLElement[] = [];

  private activeTab = 0;

  constructor(tabs: [string, HTMLElement][]) {
    assert(tabs.length > 0, "At least one tab is required");

    this.root = document.createElement("div");
    this.root.innerHTML = `
        <ul class="nav nav-underline border-bottom px-3"></ul>
        <div id="tab-content" class="p-3"></div>
    `;

    const navigation = this.root.querySelector("ul") as HTMLUListElement;
    this.content = this.root.querySelector("#tab-content") as HTMLElement;

    for (let i = 0; i < tabs.length; i++) {
      const [label, element] = tabs[i];

      const tabLink = document.createElement("a");
      tabLink.classList.add("nav-link");
      tabLink.textContent = label;
      tabLink.href = "#";
      tabLink.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (i === this.activeTab) {
          return;
        }

        this.moveToTab(i);
      });

      const tabNavItem = document.createElement("li");
      tabNavItem.classList.add("nav-item");
      tabNavItem.append(tabLink);

      if (i === 0) {
        tabLink.classList.add("active");
        this.content.replaceChildren(element);
      }

      navigation.append(tabNavItem);

      this.linkElements.push(tabLink);
      this.contentElements.push(element);
    }
  }

  private moveToTab(index: number) {
    assert(index >= 0 && index < this.linkElements.length, "Invalid tab index");

    this.linkElements[this.activeTab].classList.remove("active");

    this.activeTab = index;
    this.linkElements[this.activeTab].classList.add("active");
    this.content.replaceChildren(this.contentElements[this.activeTab]);
  }
}

class SchemaMenu {
  public root: HTMLElement;

  private ruleList: RuleList;
  private updateBasicData: (basicData: BasicData) => void;
  private updateAbleitungsmodifikationenRepraesentation: (
    value: string,
  ) => void;
  private updateAbleitungsmodifikationenStruktur: (value: string) => void;

  constructor(private store: ProjectStore) {
    const schema = store.schema;
    const disabled = schema.type === "version";

    const schemaAngabenPage = document.createElement("div");
    if (schema.type === "version") {
      const versionContainer = document.createElement("div");
      versionContainer.classList.add(
        "d-flex",
        "flex-row",
        "align-items-center",
        "gap-2",
        "mb-3",
      );

      const versionBadge = document.createElement("span");
      versionBadge.classList.add("badge", "bg-secondary");
      versionBadge.textContent = `Version ${schema.fimVersion}`;
      versionContainer.append(versionBadge);

      const fimportalLink = document.createElement("a");
      fimportalLink.classList.add(
        "d-flex",
        "align-items-center",
        "gap-2",
        "ms-auto",
      );
      fimportalLink.href = schema.url;
      fimportalLink.target = "_blank";

      const icon = document.createElement("i");
      icon.classList.add("bi", "bi-box-arrow-up-right");
      fimportalLink.append("Im FIM-Portal anzeigen", icon);

      versionContainer.append(fimportalLink);
      schemaAngabenPage.append(versionContainer);
    }

    const schemaAngabenMenu = new MenuContainer(disabled);
    schemaAngabenPage.append(schemaAngabenMenu.root);

    this.updateBasicData = schemaAngabenMenu.basicElementData(
      (editBasicData) => {
        const schema = store.schema;
        assert(
          schema.type === "arbeitskopie",
          "Cannot edit a versioned schema",
        );

        editBasicData(schema.basicData);
        store.updateSchema(schema);
      },
    );

    this.updateAbleitungsmodifikationenRepraesentation =
      schemaAngabenMenu.select(
        "Ableitungsmodifikationen Repräsentation",
        [
          ["0", "Nicht Modifizierbar"],
          ["1", "Modifizierbar"],
        ],
        (value) => {
          const schema = store.schema;
          assert(
            schema.type === "arbeitskopie",
            "Cannot edit a versioned schema",
          );

          schema.ableitungsModifikationenRepraesentation =
            parseAbleitungsmodifikationenRepraesentation(value);
          store.updateSchema(schema);
        },
      );

    this.updateAbleitungsmodifikationenStruktur = schemaAngabenMenu.select(
      "Ableitungsmodifikationen Struktur",
      [
        ["0", "Alles Modifizierbar"],
        ["1", "Nur Erweiterbar"],
        ["2", "Nur Einschränkbar"],
        ["3", "Nicht Modifizierbar"],
      ],
      (value) => {
        const schema = store.schema;
        assert(
          schema.type === "arbeitskopie",
          "Cannot edit a versioned schema",
        );

        schema.ableitungsModifikationenStrutkur =
          parseAbleitungsmodifikationenStruktur(value);
        store.updateSchema(schema);
      },
    );

    this.ruleList = new RuleList(
      store,
      disabled,
      () => store.createRule({ type: "schema", fimId: schema.fimId }),
      (ruleId) =>
        store.removeRuleFromElement(
          { type: "schema", fimId: schema.fimId },
          ruleId,
        ),
    );

    const tabMenu = new TabMenu([
      ["Beschreibung", schemaAngabenPage],
      ["Regeln", this.ruleList.root],
    ]);
    this.root = tabMenu.root;

    this.store.addEventListener(schema.fimId, this.onUpdate);
    this.update(schema);
  }

  private onUpdate = () => {
    this.update(this.store.schema);
  };

  private update(schema: Schema) {
    this.updateBasicData(schema.basicData);
    this.updateAbleitungsmodifikationenRepraesentation(
      schema.ableitungsModifikationenRepraesentation,
    );
    this.updateAbleitungsmodifikationenStruktur(
      schema.ableitungsModifikationenStrutkur,
    );
    this.ruleList.updateRules(schema.rules);
  }

  public detach() {
    this.store.removeEventListener(this.store.schema.fimId, this.onUpdate);
  }
}

class GroupMenu {
  public root: HTMLElement;

  private ruleList: RuleList;
  private updateBasicData: (basicData: BasicData) => void;

  constructor(
    private store: ProjectStore,
    private groupId: string,
  ) {
    const group = store.groups[groupId];
    const disabled = group.type === "version";

    const groupAngabenPage = document.createElement("div");
    if (group.type === "version") {
      const versionContainer = document.createElement("div");
      versionContainer.classList.add(
        "d-flex",
        "flex-row",
        "align-items-center",
        "gap-2",
        "mb-3",
      );

      const versionBadge = document.createElement("span");
      versionBadge.classList.add("badge", "bg-secondary");
      versionBadge.textContent = `Version ${group.fimVersion}`;
      versionContainer.append(versionBadge);

      const fimportalLink = document.createElement("a");
      fimportalLink.classList.add(
        "d-flex",
        "align-items-center",
        "gap-2",
        "ms-auto",
      );
      fimportalLink.href = group.url;
      fimportalLink.target = "_blank";

      const icon = document.createElement("i");
      icon.classList.add("bi", "bi-box-arrow-up-right");
      fimportalLink.append("Im FIM-Portal anzeigen", icon);

      versionContainer.append(fimportalLink);
      groupAngabenPage.append(versionContainer);
    }

    const groupAngabenMenu = new MenuContainer(disabled);
    groupAngabenPage.append(groupAngabenMenu.root);

    this.updateBasicData = groupAngabenMenu.basicElementData(
      (editBasicData) => {
        const group = store.groups[groupId];
        assert(group.type === "arbeitskopie", "Cannot edit a versioned group");

        editBasicData(group.basicData);
        store.updateGroup(group);
      },
    );

    this.ruleList = new RuleList(
      store,
      disabled,
      () => {
        return store.createRule({ type: "group", fimId: groupId });
      },
      (ruleId) => {
        store.removeRuleFromElement({ type: "group", fimId: groupId }, ruleId);
      },
    );

    const tabMenu = new TabMenu([
      ["Beschreibung", groupAngabenPage],
      ["Regeln", this.ruleList.root],
    ]);
    this.root = tabMenu.root;

    this.store.addEventListener(this.groupId, this.onUpdate);
    this.update(group);
  }

  private onUpdate = () => {
    const group = this.store.groups[this.groupId];
    this.update(group);
  };

  private update(group: Group) {
    this.updateBasicData(group.basicData);
    this.ruleList.updateRules(group.rules);
  }

  public detach() {
    this.store.removeEventListener(this.groupId, this.onUpdate);
  }
}

class RuleList {
  public root: HTMLDivElement;

  private list: HTMLDivElement;

  constructor(
    private store: ProjectStore,
    // TODO: Make `disabled`updateable
    private disabled: boolean,
    onAddRule: () => RuleArbeitskopie,
    private onRemoveRule: (ruleId: string) => void,
  ) {
    this.root = document.createElement("div");
    this.root.innerHTML = `
        <h5 class="mb-2">Regeln</h5>
        <div id="list" class="d-flex flex-column gap-2 mb-3"></div>
        <button type="button" class="btn btn-light">
          <i class="bi bi-plus"></i>
          Regel hinzufügen
        </button>
    `;

    this.list = this.root.querySelector("#list") as HTMLDivElement;

    const newRuleButton = this.root.querySelector(
      "button",
    ) as HTMLButtonElement;
    newRuleButton.addEventListener("click", () => {
      const rule = onAddRule();
      store.setActiveElement({ type: "rule", fimId: rule.fimId });
    });

    if (this.disabled) {
      newRuleButton.remove();
    }
  }

  public updateRules(rules: RuleReference[]) {
    this.list.innerHTML = "";

    if (rules.length === 0) {
      this.list.innerHTML = `
        <span class="text-muted">
          Dieses Element hat keine Regeln.
        </span>
      `;
    }

    for (const ruleRef of rules) {
      const rule = this.store.rules[ruleRef.fimId];

      const row = document.createElement("div");
      row.classList.add("d-flex", "flex-row", "align-items-center", "gap-2");

      row.innerHTML = `
        <div class="flex-grow-1 card card-link">
            <div class="card-body d-flex flex-row align-items-center justify-content-between py-2">
                <div class="d-flex flex-column">
                    <h6 class="mb-0"></h6>
                    <span class="text-muted"></span>
                </div>
                <a id="edit" href="#" class="pe-2 stretched-link"><i class="bi bi-pencil"></i></a>
            </div>
        </div>
        <a id="remove" href="#" class="btn btn-light link-danger"><i class="bi bi-trash"></i></a>
        `;

      const name = row.querySelector("h6") as HTMLElement;
      name.textContent = rule.name;

      const ruleId = row.querySelector("span") as HTMLElement;
      ruleId.textContent = rule.fimId;

      const editLink = row.querySelector("#edit") as HTMLAnchorElement;
      editLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.store.setActiveElement({ type: "rule", fimId: ruleRef.fimId });
      });

      const removeLink = row.querySelector("#remove") as HTMLAnchorElement;
      removeLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.onRemoveRule(rule.fimId);
      });

      this.list.append(row);
    }
  }
}

class FieldMenu {
  readonly root: HTMLElement;

  private ruleList: RuleList;
  private updateBasicData: (basicData: BasicData) => void;
  private updateFeldart: (value: string) => void;
  private updateDatentyp: (value: string) => void;

  constructor(
    private store: ProjectStore,
    private fieldId: string,
  ) {
    const field = store.fields[fieldId];
    const disabled = field.type === "version";

    const fieldAngabenPage = document.createElement("div");
    if (field.type === "version") {
      const versionContainer = document.createElement("div");
      versionContainer.classList.add(
        "d-flex",
        "flex-row",
        "align-items-center",
        "gap-2",
        "mb-3",
      );

      const versionBadge = document.createElement("span");
      versionBadge.classList.add("badge", "bg-secondary");
      versionBadge.textContent = `Version ${field.fimVersion}`;
      versionContainer.append(versionBadge);

      const fimportalLink = document.createElement("a");
      fimportalLink.classList.add(
        "d-flex",
        "align-items-center",
        "gap-2",
        "ms-auto",
      );
      fimportalLink.href = field.url;
      fimportalLink.target = "_blank";

      const icon = document.createElement("i");
      icon.classList.add("bi", "bi-box-arrow-up-right");
      fimportalLink.append("Im FIM-Portal anzeigen", icon);

      versionContainer.append(fimportalLink);
      fieldAngabenPage.append(versionContainer);
    }

    const fieldAngabenMenu = new MenuContainer(disabled);
    fieldAngabenPage.append(fieldAngabenMenu.root);

    this.updateBasicData = fieldAngabenMenu.basicElementData(
      (editBasicData) => {
        const field = store.fields[fieldId];
        assert(field.type === "arbeitskopie", "Cannot edit a versioned field");

        editBasicData(field.basicData);
        store.updateField(field);
      },
    );

    this.updateFeldart = fieldAngabenMenu.select(
      "Feldart",
      [
        ["input", "Eingabefeld"],
        ["label", "Hilfstext"],
        ["select", "Auswahlfeld"],
        ["hidden", "Versteckt"],
        ["locked", "Gesperrt"],
      ],
      (value) => {
        const field = store.fields[fieldId];
        assert(field.type === "arbeitskopie", "Cannot edit a versioned field");

        field.inputType = value as xdf3.Feldart;
        store.updateField(field);
      },
    );

    this.updateDatentyp = fieldAngabenMenu.select(
      "Datentyp",
      [
        ["text", "Text"],
        ["text_latin", "Text (String.Latin)"],
        ["date", "Datum"],
        ["time", "Zeit"],
        ["datetime", "Datum und Zeit"],
        ["bool", "Wahrheitswert"],
        ["num", "Zahl"],
        ["num_int", "Ganzzahl"],
        ["num_currency", "Währung"],
        ["file", "Datei"],
        ["obj", "Objekt"],
      ],
      (value) => {
        const field = store.fields[fieldId];
        assert(field.type === "arbeitskopie", "Cannot edit a versioned field");

        field.dataType = value as xdf3.Datentyp;
        store.updateField(field);
      },
    );

    this.ruleList = new RuleList(
      store,
      disabled,
      () => {
        return store.createRule({ type: "field", fimId: fieldId });
      },
      (ruleId) => {
        store.removeRuleFromElement({ type: "field", fimId: fieldId }, ruleId);
      },
    );

    const tabMenu = new TabMenu([
      ["Beschreibung", fieldAngabenPage],
      ["Regeln", this.ruleList.root],
    ]);
    this.root = tabMenu.root;

    this.store.addEventListener(this.fieldId, this.onUpdate);
    this.onUpdate();
  }

  private onUpdate = () => {
    const field = this.store.fields[this.fieldId];

    this.updateBasicData(field.basicData);
    this.updateFeldart(field.inputType);
    this.updateDatentyp(field.dataType);
    this.ruleList.updateRules(field.rules);
  };

  public detach() {
    this.store.removeEventListener(this.fieldId, this.onUpdate);
  }
}

class RuleMenu {
  public root: HTMLElement;

  private ruleEditor: RuleEditor | null = null;

  private updateName: (value: string) => void;
  private updateBeschreibung: (value?: string) => void;
  private updateFreitextRegel: (value?: string) => void;
  private updateRuleType: (value: string) => void;
  private updateTarget: (value: ElementPickerState | undefined) => void;

  constructor(
    private store: ProjectStore,
    private ruleId: string,
  ) {
    const rule = store.rules[ruleId];

    this.root = document.createElement("div");
    this.root.classList.add("card-body", "border-top", "py-2");
    this.root.innerHTML = `
      <div class="menu"></div>
    `;

    const menuContainer = new MenuContainer(rule.type === "version");
    const menu = this.root.querySelector(".menu") as HTMLElement;
    menu.append(menuContainer.root);

    this.updateName = menuContainer.textInput("Name", (value) => {
      const rule = this.store.rules[this.ruleId];
      assert(rule.type === "arbeitskopie");

      rule.name = value;
      this.store.updateRule(rule);
    });

    this.updateBeschreibung = menuContainer.textArea(
      "Beschreibung",
      (value) => {
        const rule = this.store.rules[this.ruleId];
        assert(rule.type === "arbeitskopie");

        rule.beschreibung = value;
        this.store.updateRule(rule);
      },
    );

    this.updateFreitextRegel = menuContainer.textArea("Freitext", (value) => {
      const rule = this.store.rules[this.ruleId];
      assert(rule.type === "arbeitskopie");

      rule.freitextRegel = value;
      this.store.updateRule(rule);
    });

    if (import.meta.env.MODE === "production") {
      // Only show the target picker in development mode.
      this.updateTarget = () => {};
      this.updateRuleType = () => {};
    } else {
      this.updateTarget = menuContainer.elementPicker("Regelziel", () => {
        this.store.enterPickMode((target) => {
          const rule = this.store.rules[this.ruleId];
          assert(rule.type === "arbeitskopie");

          const parent = this.store.getRuleParent(this.ruleId);
          const relativePath = getRelativeChildPath(parent, target);

          rule.target = relativePath;
          this.store.updateRule(rule);
        });
      });

      this.updateRuleType = menuContainer.select(
        "Regeltyp",
        [
          ["valid", "Gültigkeitsregel"],
          ["active", "Aktivitätsregel"],
        ],
        (value) => {
          const rule = this.store.rules[this.ruleId];
          assert(rule.type === "arbeitskopie");

          rule.ruleType = value as "valid" | "active";
          this.store.updateRule(rule);
        },
      );

      if (rule.type === "arbeitskopie") {
        this.ruleEditor = new RuleEditor(
          rule.expression,
          (expression) => {
            rule.expression = expression;
            this.store.updateRule(rule);
          },
          (onPick: (value: string) => void) => {
            this.store.enterPickMode((target) => {
              const parent = this.store.getRuleParent(this.ruleId);
              const relativePath = getRelativeChildPath(parent, target);

              const path = relativePath
                .map((element) => element.fimId)
                .join(".");
              onPick(path);
            });
          },
        );

        const label = document.createElement("label");
        label.classList.add("form-label", "mb-2", "fw-semibold");
        label.textContent = "Regelbedingung";
        label.htmlFor = "rule-editor";

        const ruleEditorContainer = document.createElement("div");
        ruleEditorContainer.classList.add("mb-3");

        ruleEditorContainer.append(label, this.ruleEditor.root);
        this.root.append(ruleEditorContainer);
      }
    }

    this.store.addEventListener(this.ruleId, this.onUpdate);
    this.update(rule);
  }

  private onUpdate = () => {
    const rule = this.store.rules[this.ruleId];
    this.update(rule);
  };

  private update(rule: Rule) {
    this.updateName(rule.name);
    this.updateBeschreibung(rule.beschreibung);
    this.updateFreitextRegel(rule.freitextRegel);
    this.updateRuleType(rule.ruleType);

    if (rule.type === "arbeitskopie") {
      if (rule.target === undefined) {
        this.updateTarget(undefined);
      } else {
        const targetElement = rule.target[rule.target.length - 1];
        let label = "";
        if (targetElement.type === "schema") {
          const schema = this.store.schema;
          label = schema.basicData.name;
        } else if (targetElement.type === "group") {
          const group = this.store.groups[targetElement.fimId];
          label = group.basicData.name;
        } else {
          assert(targetElement.type === "field");
          const field = this.store.fields[targetElement.fimId];
          label = field.basicData.name;
        }

        this.updateTarget({
          label,
          path: rule.target.map((element) => element.fimId).join(" / "),
        });
      }
    } else {
      this.updateTarget(undefined);
    }
  }

  public detach() {
    this.store.removeEventListener(this.ruleId, this.onUpdate);
  }
}

export class Input {
  public root: HTMLElement;

  private input: HTMLInputElement;

  constructor(name: string, value: string, helpText?: string) {
    this.root = document.createElement("div");
    this.root.classList.add("mb-3");

    this.root.innerHTML = `
            <label for="name" class="form-label"></label>
            <input type="text" class="form-control form-control-sm" />
        `;

    const label = this.root.querySelector("label") as HTMLLabelElement;
    label.innerHTML = name;

    this.input = this.root.querySelector("input") as HTMLInputElement;
    this.input.value = value;

    if (helpText !== undefined) {
      const helpContainer = document.createElement("div");
      helpContainer.classList.add("form-text");
      helpContainer.textContent = helpText;

      this.root.append(helpContainer);
    }
  }

  public getValue(): string | undefined {
    const sanitized = this.input.value.trim();

    if (sanitized.length > 0) {
      return sanitized;
    } else {
      return undefined;
    }
  }

  public setValue(value: string | undefined) {
    if (value !== undefined) {
      this.input.value = value;
    } else {
      this.input.value = "";
    }
  }
}
